import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
  postQueryBodyWithCreator,
} from "../../../utils/reduxHelper";

export const sourcingProductReduxAPI = createApi({
  reducerPath: "sourcingProductReduxAPI",
  baseQuery: baseQueryWithArgs,

  tagTypes: [
    "productDetails",
    "productColorOptions",
    "productVariants",
    "productReaction",
  ],

  endpoints: (builder) => ({
    //GET Queries
    getProductDetails: builder.query({
      query: ({ productId, isBrandSourcing }) =>
        getQueryStringWithCreator(
          `s/getProductDetails?productId=${productId}&isBrandSourcing=${isBrandSourcing}`
        ),
      providesTags: ["productDetails"],
    }),
    getProductVariants: builder.query({
      query: ({ productId, isBrandSourcing }) => {
        return getQueryStringWithCreator(
          `s/getProductVariants?productId=${productId}&isBrandSourcing=${isBrandSourcing}`
        );
      },
      providesTags: ["productVariants"],
    }),
    getProductColorOptions: builder.query({
      query: ({ productId }) =>
        getQueryStringWithCreator(
          `s/getProductColorOptions?productId=${productId}`
        ),
      providesTags: ["productColorOptions"],
    }),
    getProductSizeChart: builder.query({
      query: ({ productId }) =>
        getQueryStringWithCreator(
          `s/getProductSizeChart?productId=${productId}`
        ),
    }),
    /** PDP sidecars (creator PDP). Backed by sourcing/views/pdp_views.py. */
    getProductReelsForPdp: builder.query({
      query: ({ productId, page = 1, limit = 10 }) =>
        getQueryStringWithCreator(
          `s/getProductReelsForPdp?productId=${productId}&page=${page}&limit=${limit}`
        ),
      transformResponse: (res) =>
        res?.data || { content: [], page: 1, done: true },
    }),
    getProductAttributesForPdp: builder.query({
      query: ({ productId }) =>
        getQueryStringWithCreator(
          `s/getProductAttributesForPdp?productId=${productId}`
        ),
      transformResponse: (res) =>
        res?.data || { description: "", productDetail: {}, reviews: null },
    }),
    getSimilarProductsForPdp: builder.query({
      query: ({ productId, page = 1, limit = 10 }) =>
        getQueryStringWithCreator(
          `s/getSimilarProductsForPdp?productId=${productId}&page=${page}&limit=${limit}`
        ),
      transformResponse: (res) =>
        res?.data || { products: [], page: 1, done: true },
    }),
    getTrendProductsForPdp: builder.query({
      // `trendId` is the trend the creator arrived from; the backend prefers it when
      // the product belongs to it, else falls back to the highest-ranked live trend.
      // Paged (10 a page, backend cap _MAX_TREND_PRODUCTS): pages merge into one cache
      // entry per (product, trend), same pattern as the PLP grid.
      query: ({ productId, trendId, page = 1, limit = 10 }) =>
        getQueryStringWithCreator(
          `s/getTrendProductsForPdp?productId=${productId}&page=${page}&limit=${limit}${
            Number(trendId) > 0 ? `&trendId=${trendId}` : ""
          }`
        ),
      transformResponse: (res) => {
        const d = res?.data || {};
        return {
          products: d.products || [],
          done: d.done !== false,
          trendId: d.trend_id ?? d.trendId ?? null,
          trendName: d.trend_name ?? d.trendName ?? null,
          trendRank: d.trend_rank ?? d.trendRank ?? null,
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs.productId}|${queryArgs.trendId || 0})`,
      merge: (currentCache, newItems, { arg }) => {
        const seen = new Set(currentCache.products.map((p) => p.id));
        const fresh = newItems.products.filter((p) => !seen.has(p.id));
        if (arg.page > 1) {
          currentCache.products.push(...fresh);
        } else {
          currentCache.products = newItems.products;
        }
        currentCache.done = newItems.done;
        currentCache.trendId = newItems.trendId;
        currentCache.trendName = newItems.trendName;
        currentCache.trendRank = newItems.trendRank;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.page !== previousArg?.page,
    }),
    /** SEL-1759: the creator's own like/dislike on this product (PDP pill). */
    getProductReactionForPdp: builder.query({
      query: ({ productId }) =>
        getQueryStringWithCreator(
          `s/getProductReactionForPdp?productId=${productId}`
        ),
      transformResponse: (res) => res?.data || { reaction: null, reasons: [] },
      providesTags: (result, error, { productId }) => [
        { type: "productReaction", id: productId },
      ],
    }),
    setProductReaction: builder.mutation({
      // body: { productId, reaction: "LIKE" | "DISLIKE" | null, reasons?: string[] }
      query: (body) => ({
        url: "s/setProductReaction",
        method: "POST",
        body: postQueryBodyWithCreator(body),
      }),
      // Write the confirmed state into the GET cache so a PDP revisit inside the
      // refetch window shows what the creator just tapped. The patch only lands
      // on a settled cache entry, so the tag below also refetches once the write
      // is done: a GET that was in flight during the tap cannot leave its old row
      // behind.
      invalidatesTags: (result, error, { productId }) =>
        result?.success ? [{ type: "productReaction", id: productId }] : [],
      onQueryStarted: async (
        { productId, reaction, reasons = [] },
        { dispatch, queryFulfilled }
      ) => {
        try {
          const { data } = await queryFulfilled;
          if (!data?.success) return;
          dispatch(
            sourcingProductReduxAPI.util.updateQueryData(
              "getProductReactionForPdp",
              { productId },
              (draft) => {
                draft.reaction = reaction ?? null;
                draft.reasons = reaction === "DISLIKE" ? reasons : [];
              }
            )
          );
        } catch (e) {
          // Failed write: the hook reverts its own optimistic state.
        }
      },
    }),
    getPdpServiceability: builder.query({
      query: ({ productId, pincode, variantId }) => {
        let url = `s/getPdpServiceability?productId=${productId}&pincode=${pincode}`;
        if (variantId != null) url += `&variantId=${variantId}`;
        return getQueryStringWithCreator(url);
      },
      transformResponse: (res) => res?.data ?? res,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLazyGetProductDetailsQuery,
  useLazyGetProductVariantsQuery,
  useLazyGetProductColorOptionsQuery,
  useLazyGetProductSizeChartQuery,
  useGetProductReelsForPdpQuery,
  useGetProductAttributesForPdpQuery,
  useGetSimilarProductsForPdpQuery,
  useGetTrendProductsForPdpQuery,
  useLazyGetPdpServiceabilityQuery,
  useGetProductReactionForPdpQuery,
  useSetProductReactionMutation,
} = sourcingProductReduxAPI;
